import EditorActions from 'components/Common/EditorActions'
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useFormik } from 'formik'
import { Container, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap'
import { Link, Prompt } from 'react-router-dom'
import Breadcrumbs from 'components/Common/Breadcrumb'
import { store as socketStore } from 'components/Common/Socket/store'
import { get, post } from 'helpers/api_helper'
import { newPlayerOperationId as newOperationId, readPending, savePending } from '../PlayerAccounts/playerInvitationState'
import { canManageAdmins } from './adminDisplay'
import { adminListPath } from './listQuery'
import { runAdminCreationCommand } from './adminCreationOperation'

export function CreateAdmin({ history, location = {}, userDetails = {} }) {
  const permitted = canManageAdmins(userDetails)
  const back = adminListPath(location.state?.adminListSearch)
  const { socket, socketClientId } = useContext(socketStore)?.state?.socket || {}
  const storageKey = 'sportya-admin-creation:' + userDetails.id
  const pending = useRef(readPending(storageKey))
  const busy = useRef(false), request = useRef(null)
  const [status, setStatus] = useState(pending.current ? 'unknown' : 'idle')
  const [options, setOptions] = useState(null)
  const [optionError, setOptionError] = useState('')
  const [retry, setRetry] = useState(0)
  const [message, setMessage] = useState('')
  const [exit, setExit] = useState(null)
  const leaving = useRef(false)
  const locked = status === 'pending' || status === 'unknown'
  useEffect(() => {
    if (!permitted) return undefined
    let current = true
    setOptions(null); setOptionError('')
    get('/admin/admin-creation-options').then(({ data }) => {
      if (!Array.isArray(data?.languages) || !data.languages.length) throw new Error('Unavailable languages')
      if (current) setOptions(data)
    }).catch(() => { if (current) setOptionError('Email languages could not be loaded. Retry before creating the account.') })
    return () => { current = false }
  }, [permitted, retry])
  const track = useCallback(async (attempt, mode = 'check') => {
    if (!permitted || busy.current || !attempt) return
    if (mode !== 'check') attempt = { ...attempt, correlationId: newOperationId() }
    busy.current = true; pending.current = attempt
    savePending(storageKey, attempt); setStatus('pending'); setMessage('')
    const operation = runAdminCreationCommand({ ...attempt, socket,
      read: id => get('/admin/admin-creations/' + id),
      submit: mode === 'create' ? () => post('/admin/accounts', { ...attempt.payload, operationId: attempt.operationId, socketClientId },
        { headers: { 'X-Correlation-Id': attempt.correlationId } }) : mode === 'retry' ? () => post('/admin/admin-creations/' + attempt.operationId + '/retry',
        { socketClientId }, { headers: { 'X-Correlation-Id': attempt.correlationId } }) : undefined,
    })
    request.current = operation
    const result = await operation.promise
    if (request.current !== operation) return
    request.current = null; busy.current = false
    if (result.status === 'cancelled') return
    if (result.code === 'CREATION_NOT_RECEIVED') {
      setStatus('resuming'); setMessage('The request did not reach the creation service. Re-enter the original details to resume the same operation.'); return
    }
    setStatus(result.status)
    if (result.status === 'saved' || result.status === 'error') { pending.current = null; savePending(storageKey, null) }
    if (result.status === 'saved') {
      leaving.current = true
      history.push({ pathname: '/accounts/admins/' + result.id,
        state: { adminListSearch: location.state?.adminListSearch, adminCreationResult: { invitationStatus: result.invitationStatus, indexStatus: result.indexStatus } } })
    } else if (result.status === 'error') setMessage(result.message)
  }, [permitted, socket, socketClientId, storageKey, history, location.state?.adminListSearch])
  useEffect(() => {
    const timer = setTimeout(() => { if (pending.current) track(pending.current) }, 0)
    return () => { clearTimeout(timer); const operation = request.current; request.current = null; busy.current = false; operation?.cancel() }
  }, [track])
  const form = useFormik({
    initialValues: { firstName: '', lastName: '', email: '', language: 'en', role: 'admin', confirmSuperAdmin: false },
    validate: values => {
      const errors = {}
      for (const [name, label] of [['firstName', 'First name'], ['lastName', 'Last name']]) {
        if (!values[name].trim() || values[name].trim().length > 120) errors[name] = label + ' is required and must contain no more than 120 characters.'
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email.trim()) || values.email.trim().length > 254) errors.email = 'Enter a valid email address.'
      if (!options?.languages.some(item => item.code === values.language)) errors.language = 'Choose an available email language.'
      if (!['admin', 'superAdmin'].includes(values.role)) errors.role = 'Choose an administrative role.'
      if (values.role === 'superAdmin' && !values.confirmSuperAdmin) errors.confirmSuperAdmin = 'Confirm SuperAdmin access before creating this account.'
      return errors
    },
    onSubmit: async values => {
      if (!permitted || locked || busy.current || !options) return
      return track({ ...(pending.current || { operationId: newOperationId() }), payload: {
        firstName: values.firstName.trim(), lastName: values.lastName.trim(), email: values.email.trim().toLowerCase(), role: values.role, language: values.language,
      } }, 'create')
    },
  })
  const unsaved = permitted && status !== 'saved' && (form.dirty || locked)
  useEffect(() => {
    if (!unsaved) return undefined
    const beforeUnload = event => { event.preventDefault(); event.returnValue = '' }
    window.addEventListener('beforeunload', beforeUnload)
    return () => window.removeEventListener('beforeunload', beforeUnload)
  }, [unsaved])
  const field = (name, label, { choices, ...props } = {}) => {
    const error = (form.submitCount > 0 || form.touched[name]) && form.errors[name]
    const input = { id: 'admin-' + name, name, value: form.values[name], onChange: event => { if (name === 'role') form.setFieldValue('confirmSuperAdmin', false); form.handleChange(event) }, onBlur: form.handleBlur,
      className: 'form-control', 'aria-required': true, 'aria-invalid': Boolean(error), 'aria-describedby': 'admin-' + name + '-error', ...props }
    return <div className="col-md-6"><div className="form-group"><label htmlFor={input.id}>{label} <span aria-hidden="true">*</span></label>
      {choices ? <select {...input}>{!choices.some(item => item.code === input.value) && <option value="">Choose an option</option>}{choices.map(item => <option key={item.code} value={item.code}>{item.name}</option>)}</select> : <input {...input} />}
      <small className="text-danger" id={'admin-' + name + '-error'}>{error}</small></div></div>
  }
  return <div className="page-content"><Container fluid>
    <Prompt when={unsaved} message={(target, action) => {
      if (leaving.current) return true
      setExit({ target, action }); return false
    }} />
    <Breadcrumbs title="Admins" path={back} breadcrumbItem="Create admin" />
    {!permitted ? <p role="alert">Only an activated SuperAdmin can create administrative accounts. <Link to={back}>Back to admins</Link></p> :
      <form aria-label="Create admin" noValidate onSubmit={async event => {
        event.preventDefault()
        const invalid = await form.validateForm()
        document.getElementById('admin-' + Object.keys(invalid)[0])?.focus()
        form.handleSubmit(event)
      }}>
        <p className="text-muted">Create an administrative account and send an invitation to set its password. Access stays pending until the invitation is completed.</p>
        {optionError && <div role="alert" className="alert alert-danger">{optionError} <button type="button" className="btn btn-light ml-2" onClick={() => setRetry(value => value + 1)}>Retry email languages</button></div>}
        {!options && !optionError && <p role="status">Loading email languages…</p>}
        {message && <p role="alert" className="text-danger">{message}</p>}
        <fieldset disabled={locked || !options}>
          <section className="card"><div className="card-body"><h5 className="mb-3">Account details</h5><div className="row">
            {field('firstName', 'First name', { autoComplete: 'given-name', maxLength: 120 })}
            {field('lastName', 'Last name', { autoComplete: 'family-name', maxLength: 120 })}
            {field('email', 'Email address', { type: 'email', autoComplete: 'email', maxLength: 254 })}
            {field('language', 'Email language', { choices: options?.languages || [] })}
          </div><small className="text-muted">The invitation goes to this address in the selected language. Its link is valid for 24 hours and can be used once.</small></div></section>
          <section className="card"><div className="card-body"><h5 className="mb-3">Administrative access</h5><div className="row">
            {field('role', 'Role', { choices: [{ code: 'admin', name: 'Admin' }, { code: 'superAdmin', name: 'SuperAdmin' }] })}
          </div><p>{form.values.role === 'superAdmin'
              ? 'SuperAdmins can manage administrative accounts and access all restricted management areas, including payments, analytics and system settings.'
              : 'Admins can manage daily operations. Administrative accounts are read-only and SuperAdmin-only management areas remain restricted.'}</p>
          {form.values.role === 'superAdmin' && <div className="form-group mb-0">
            <label className="d-flex align-items-center" htmlFor="admin-confirmSuperAdmin"><input id="admin-confirmSuperAdmin" name="confirmSuperAdmin" type="checkbox" className="mr-2"
              checked={form.values.confirmSuperAdmin} onChange={form.handleChange} aria-invalid={Boolean(form.submitCount && form.errors.confirmSuperAdmin)} aria-describedby="admin-confirmSuperAdmin-error" />I confirm granting SuperAdmin access</label>
            <small id="admin-confirmSuperAdmin-error" className="text-danger">{form.submitCount > 0 && form.errors.confirmSuperAdmin}</small>
          </div>}
          </div></section>
        </fieldset>
        {status === 'pending' && <p role="status">Waiting for account creation confirmation…</p>}
        {status === 'unknown' && <div className="alert alert-warning" role="status"><p>The result is not confirmed yet. Check its status before creating another account.</p>
          <button type="button" className="btn btn-light mr-2" onClick={() => track(pending.current)}>Check status</button>
          <button type="button" className="btn btn-primary" onClick={() => track(pending.current, 'retry')}>Resume creation</button></div>}
        <EditorActions dirty={form.dirty} pending={locked} saveLabel="Create admin & send invitation">
          <button className="btn btn-light d-inline-flex align-items-center justify-content-center mb-2" type="button" onClick={() => history.push(back)}>Cancel</button>
          <button className="btn btn-primary d-inline-flex align-items-center justify-content-center mr-2 mb-2" type="submit" disabled={locked || !options}>Create admin &amp; send invitation</button>
        </EditorActions>
      </form>}
    <Modal isOpen={Boolean(exit)} toggle={() => setExit(null)}><ModalHeader toggle={() => setExit(null)}>Leave admin creation?</ModalHeader>
      <ModalBody>{locked ? 'The request may already be saved. Its operation identifier will be kept so you can check the result when you return.' : 'Your unsaved account details will be lost.'}</ModalBody>
      <ModalFooter><button type="button" className="btn btn-light" onClick={() => setExit(null)}>Keep editing</button>
        <button type="button" className="btn btn-danger" onClick={() => {
          leaving.current = true; const destination = exit; setExit(null)
          if (destination.action === 'REPLACE') history.replace(destination.target)
          else history.push(destination.target)
        }}>Leave page</button></ModalFooter>
    </Modal>
  </Container></div>
}

export default function CreateAdminPage(props) {
  const userDetails = useSelector(state => state.session.userDetails) || {}
  return <CreateAdmin key={userDetails.id || ''} {...props} userDetails={userDetails} />
}
